import { CircleSlash, Database } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { buildVisualForAggregate, type SourceHealthVisual } from '@/components/source-health-visual.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api, type SourceHealthResponse } from '@/lib/api.js';
import { cn } from '@/lib/utils';

/**
 * Source-Health-Lampe fuer den Topbar.
 *
 * - Polling alle 60s (POLL_INTERVAL_MS) via `GET /api/catalog/sources/health`.
 * - Farbe nach `aggregate.worst`: gruen (ok), gelb (stale), rot (error).
 * - Tooltip: kurze Aufschluesselung total/ok/stale/error.
 * - Klick: navigiert zur Sources-Uebersicht (/sources).
 * - Bei Lade-/Netzwerk-Fehler: graues Slash-Icon, kein lautes Banner.
 */

const POLL_INTERVAL_MS = 60_000;

type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: SourceHealthResponse };

export function SourceHealthIndicator(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await api.sourceHealth();
        if (cancelled) return;
        if (data.items.length === 0) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'ready', data });
        }
      } catch {
        if (!cancelled) setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
      }
    };
    void load();
    const handle = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  const visual = pickVisual(state);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => navigate('/sources')}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-md outline-none ring-offset-background transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            aria-label={`Quellen: ${visual.aria}`}
          >
            <visual.Icon className={cn('h-5 w-5', visual.colorClass)} aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          <div className="text-xs">
            <div className="font-semibold">Quellen</div>
            <div className="mt-1 text-muted-foreground">{visual.tooltip}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function pickVisual(state: State): SourceHealthVisual {
  if (state.kind === 'loading') {
    return {
      Icon: Database,
      colorClass: 'text-muted-foreground',
      aria: 'wird geladen',
      tooltip: 'Status wird geladen…',
    };
  }
  if (state.kind === 'error') {
    return {
      Icon: CircleSlash,
      colorClass: 'text-muted-foreground',
      aria: 'unbekannt',
      tooltip: 'Health-Status nicht verfügbar.',
    };
  }
  if (state.kind === 'empty') {
    return {
      Icon: Database,
      colorClass: 'text-muted-foreground',
      aria: 'keine Quellen',
      tooltip: 'Keine externen Quellen konfiguriert.',
    };
  }
  const { aggregate } = state.data;
  return buildVisualForAggregate(aggregate.worst, aggregate);
}


